import { CollapseSection } from "@msys/ui";
import { Box, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  OrganisationProjectsSorting,
  ProjectStateMachineStatus,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../auth/RestrictedByCapability.js";
import { FilterModal } from "../../commons/filters/FilterModal.js";
import { FilterCheckbox } from "../../commons/filters/index.js";
import { CrmCompaniesOrPersonsSelect } from "../crm-companies/CrmCompaniesOrPersonsSelect.js";
import { CrmCompanySelect } from "../crm-companies/CrmCompanySelect.js";
import { FilterCustomFields } from "../custom-fields/filters/index.js";
import { useCustomFieldConfig } from "../custom-fields/useCustomFieldConfig.js";
import { FilterByOrganisationUsers } from "../users/FilterByOrganisationUsers.js";
import {
  FilterBuildingZip,
  FilterPlanningState,
  FilterProjectCategory,
  FilterProjectState,
  FilterProjectType,
  FilterRelevantToMe,
  FilterWithinRadiusKm,
  ProjectsSorting,
} from "./filters/index.js";
import { TeamsFilter } from "../teams/filters/TeamsFilter.js";
import { FilterAssignees } from "./filters/ProjectAssignees.js";
import { FilterProjectPhases } from "./filters/ProjectPhases.js";
import { ProjectSourcesSelect } from "./filters/ProjectSources.js";
import { DEFAULT_SORTING, Filters } from "./ProjectsList.js";
import { omit } from "@msys/common";

export function ProjectsFilterModal<F extends Filters>({
  handleClose,
  sorting,
  setSorting,
  filters,
  setFilters,
  resetFilters,
  allowedStates,
  showSorting,
  showPhasesFilter,
}: {
  handleClose: () => void;
  sorting: OrganisationProjectsSorting[];
  setSorting: (sorting: OrganisationProjectsSorting[]) => void;
  filters: F;
  setFilters: React.Dispatch<React.SetStateAction<F>>;
  resetFilters: () => void;
  allowedStates: ProjectStateMachineStatus[];
  showSorting: boolean;
  showPhasesFilter: boolean;
}) {
  const { t } = useTranslate(["Projects", "Tickets", "Global"]);

  const { customFieldConfig: customFieldConfigs } =
    useCustomFieldConfig("Project");

  const [filtersState, setFiltersState] = React.useState(filters);
  const [sortingState, setSortingState] = React.useState(sorting);

  return (
    <FilterModal
      title={t("Project Filters", {
        ns: "Projects",
      })}
      handleApply={() => {
        setSorting(sortingState);
        setFilters(filtersState);
      }}
      handleClose={handleClose}
      handleReset={() => resetFilters()}
      spacing={2}
    >
      {showSorting && (
        <ProjectsSorting
          sorting={sortingState[0]}
          setSorting={setSortingState}
          defaultSorting={DEFAULT_SORTING}
        />
      )}

      <CollapseSection
        title={t("Scope", { ns: "Projects" })}
        isInitiallyExpanded={true}
      >
        <Stack direction="column" spacing={1}>
          <FilterProjectCategory
            value={filtersState.filterCategories ?? []}
            setValue={newValue =>
              setFiltersState(state => ({
                ...state,
                filterCategories: newValue,
              }))
            }
          />
          <FilterProjectType
            value={filtersState.filterType ?? []}
            setValue={newValue =>
              setFiltersState(filters => ({
                ...filters,
                filterType: newValue,
              }))
            }
          />
          <ProjectSourcesSelect
            value={filtersState.sources ?? []}
            setValue={newValue =>
              setFiltersState(filters => ({
                ...filters,
                sources: newValue,
              }))
            }
          />
          <FilterProjectState
            allowedStates={allowedStates}
            value={filtersState.filterIncludeState ?? []}
            setValue={newValue =>
              newValue.length > 0
                ? setFiltersState(filters => ({
                    ...filters,
                    filterIncludeState: newValue,
                  }))
                : setFilters(
                    filters => omit(filters, "filterIncludeState") as F
                  )
            }
          />
        </Stack>
      </CollapseSection>

      <CollapseSection
        title={t("Location", { ns: "Projects" })}
        isInitiallyExpanded={true}
      >
        <Stack direction="column" spacing={1}>
          <FilterBuildingZip
            value={filtersState.filterBuildingZip ?? ""}
            setValue={newValue =>
              setFiltersState(state => ({
                ...state,
                filterBuildingZip: newValue,
              }))
            }
          />
          <FilterWithinRadiusKm
            value={filtersState.filterWithinRadiusKm ?? 0}
            setValue={newValue =>
              setFiltersState(state => ({
                ...state,
                filterWithinRadiusKm: newValue,
              }))
            }
          />
        </Stack>
      </CollapseSection>

      <CollapseSection
        title={t("People", { ns: "Projects" })}
        isInitiallyExpanded={true}
      >
        <Stack direction="column" spacing={1}>
          <RestrictedByCapabilityWithDebug capability="quoting">
            <CrmCompanySelect
              crmCompanyId={filtersState.filterByCrmOrganisationId ?? undefined}
              onChange={crmCompany =>
                setFiltersState(state => ({
                  ...state,
                  filterByCrmOrganisationId: crmCompany?.id,
                }))
              }
              inputLabel={t("Client", { ns: "Projects" })}
              required={false}
              canCreateNew={false}
              disableBlockedBusinessPartners={false}
            />
          </RestrictedByCapabilityWithDebug>
          <CrmCompaniesOrPersonsSelect
            crmCompaniesOrPersonsIds={filtersState.filterByCrmLinkIds ?? []}
            onChange={crmCompaniesOrPersonsIds =>
              setFiltersState(state => ({
                ...state,
                filterByCrmLinkIds: crmCompaniesOrPersonsIds,
              }))
            }
            inputLabel={t("Other contacts", { ns: "Projects" })}
            required={false}
          />
          <FilterByOrganisationUsers
            inputLabel={t("Created by", { ns: "Projects" })}
            // placeholder=""
            value={filtersState.filterBySourceUserIds ?? []}
            setValue={newValue => {
              setFiltersState(filters => ({
                ...filters,
                filterBySourceUserIds: newValue,
              }));
            }}
          />
          <TeamsFilter
            value={filtersState.filterTeamIds ?? []}
            setValue={newValue =>
              setFiltersState(state =>
                newValue.length > 0
                  ? { ...state, filterTeamIds: newValue }
                  : (omit(state, "filterTeamIds") as F)
              )
            }
          />
        </Stack>
      </CollapseSection>

      <CollapseSection
        title={t("Operations", { ns: "Projects" })}
        isInitiallyExpanded={true}
      >
        <Stack direction="column" spacing={1}>
          <Stack direction="row" spacing={1}>
            <Box>
              <FilterCheckbox
                label={t("Urgent", { ns: "Tickets" })}
                value={filtersState.filterUrgent ?? undefined}
                setValue={newValue =>
                  setFiltersState(state => ({
                    ...state,
                    filterUrgent: newValue,
                  }))
                }
              />
            </Box>
            <Box>
              <FilterCheckbox
                label={t("Overdue", { ns: "Tickets" })}
                value={filtersState.filterOverdue ?? undefined}
                setValue={newValue =>
                  setFiltersState(state => ({
                    ...state,
                    filterOverdue: newValue,
                  }))
                }
              />
            </Box>
          </Stack>
          {showPhasesFilter && (
            <FilterProjectPhases
              allowedStates={allowedStates}
              value={filtersState.filterPhaseIds ?? []}
              setValue={newValue =>
                setFiltersState(filters => ({
                  ...filters,
                  filterPhaseIds: newValue,
                }))
              }
            />
          )}
          <FilterCheckbox
            label={t("Unassigned", { ns: "Projects" })}
            value={filtersState.filterUnassigned ?? false}
            setValue={newValue =>
              setFiltersState(state => ({
                ...state,
                filterUnassigned: newValue || undefined,
              }))
            }
          />
          <FilterAssignees
            value={filtersState.filterAssigneeIds ?? []}
            setValue={newValue =>
              setFiltersState(filters => ({
                ...filters,
                filterAssigneeIds: newValue,
              }))
            }
          />
          <RestrictedByCapabilityWithDebug capability="planning">
            <FilterPlanningState
              value={filtersState.filterPlanningState ?? []}
              setValue={newValue =>
                newValue.length > 0
                  ? setFiltersState(state => ({
                      ...state,
                      filterPlanningState: newValue,
                    }))
                  : setFiltersState(
                      filters => omit(filters, "filterPlanningState") as F
                    )
              }
            />
          </RestrictedByCapabilityWithDebug>
        </Stack>
      </CollapseSection>

      {/*<FilterMinBudget
        value={filtersState.filterMinBudget ?? 0}
        setValue={newValue =>
          setFiltersState(state => ({
            ...state,
            filterMinBudget: newValue,
          }))
        }
      />*/}

      {customFieldConfigs.length > 0 && (
        <FilterCustomFields
          fieldConfigs={customFieldConfigs}
          values={filtersState.filterByCustomFields ?? []}
          setValues={newValues =>
            setFiltersState(state => ({
              ...state,
              filterByCustomFields: newValues,
            }))
          }
        />
      )}

      <Box>
        <FilterRelevantToMe
          filters={filtersState}
          setFilters={setFiltersState}
        />
      </Box>
    </FilterModal>
  );
}
